import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { resolvePostLogin, loginHrefFor } from './lib/returnTo';
import { useUserContext } from './hooks/useUserContext';
import { shouldRequireOnboarding } from './utils/onboarding';
import { useCourseTesterAccess } from './hooks/useCourseTesterAccess';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import Layout from './components/Layout';
import AppErrorBoundary from './components/AppErrorBoundary';
import ApiBridge from './components/ApiBridge';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import AuthCallback from './pages/AuthCallback';
import InvitationAcceptance from './pages/InvitationAcceptance';
// SIGNUP-GATE (Cluster D) · the blocked-user screen. Kept eager (not lazy) like
// Login: it sits on the auth-critical path and must render instantly, with no
// chunk-load flash, for a signed-in student whose region is on the waitlist.
import Waitlisted from './pages/Waitlisted';

// Lazy-load heavier route-level pages so the initial bundle (Login, Layout,
// NotFound) stays small. IDE in particular pulls in Monaco (~600 KB gzipped)
// plus the entire teaching-agent/collab surface, so deferring it keeps the
// Dashboard and Labs list cheap to load.
const IDE = lazy(() => import('./pages/IDE'));
const Catalog = lazy(() => import('./pages/Catalog'));
const MyLearning = lazy(() => import('./pages/MyLearning'));
// NOTE: CoursePage / LearningItemPage are the LEGACY course-detail surface.
// Their component files stay in the repo, but the student-facing routes below
// now redirect into the polished /portal/* shells (see Legacy*Redirect), so
// they're no longer mounted directly.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const CredentialsPage = lazy(() => import('./pages/CredentialsPage'));
const Grades = lazy(() => import('./pages/Grades'));

// MV5 Wave 1 · Portal + Onboarding surfaces. These sit alongside the
// existing dashboard/catalog/IDE routes rather than replacing them — the
// IDE stack stays untouched so /lab/:labId keeps working exactly as it
// did before Wave 1. See docs/design-context/mv5-review.html for the
// Figma source of truth.
const PortalLayout = lazy(() => import('./pages/portal/PortalLayout'));
const PortalHome = lazy(() => import('./pages/portal/PortalHome'));
const PortalSettings = lazy(() => import('./pages/portal/PortalSettings'));
// Figma page 04 · Student Portal — Cert Overview / Course Detail / AI Tutor /
// Grades routes. These live inside web-ide today; the long-term plan (per the
// multi-TLD arch doc) moves them to portal.hauraki.ai. The paths were picked
// so the eventual TLD move is a routing swap, not a component rewrite.
const PortalCertOverview = lazy(() => import('./pages/portal/PortalCertOverview'));
// Real index behind the "Program" nav item (replaces the old dead /portal/certs
// which rendered "Certification not found"). Shows the enrolled track's cert
// ladder — done / current / locked.
const PortalProgram = lazy(() => import('./pages/portal/PortalProgram'));
const PortalHelp = lazy(() => import('./pages/portal/PortalHelp'));
const PortalCourseDetail = lazy(() => import('./pages/portal/PortalCourseDetail'));
// MV5 Phase 2c · `/portal/lab/:labId` opens the ShellRouter with the lab
// pre-selected. Sibling export of PortalCourseDetail because both routes
// resolve to the same shell — Wrapper just seeds a different `initialLabId`.
const ShellLabWrapper = lazy(() =>
  import('./pages/portal/PortalCourseDetail').then((m) => ({
    default: m.ShellLabWrapper,
  })),
);
const PortalTutor = lazy(() => import('./pages/portal/PortalTutor'));
const PortalGrades = lazy(() => import('./pages/portal/PortalGrades'));
const PortalPortfolio = lazy(() => import('./pages/portal/PortalPortfolio'));
// T1 buy→learn spine — enroll handoff + post-Stripe return. Top-level (not
// under /portal) so the marketing site and Stripe can link straight in.
const PortalCheckout = lazy(() => import('./pages/portal/PortalCheckout'));
const PortalCheckoutReturn = lazy(() => import('./pages/portal/PortalCheckoutReturn'));
// PK direct wallet integration — post-JazzCash return (backend redirects
// here after independently verifying pp_SecureHash; see JazzCashService).
const PortalJazzCashReturn = lazy(() => import('./pages/portal/PortalJazzCashReturn'));
// MV5 · Portal · Take Quiz. `/portal/quiz/:courseItemId` renders the reused
// AssessmentRunner inside portal chrome — the canonical quiz surface now that
// the legacy LearningItemPage (which used to mount the runner) is redirected.
const PortalQuizPage = lazy(() => import('./pages/portal/PortalQuizPage'));
const OnboardingLayout = lazy(() => import('./pages/onboarding/OnboardingLayout'));
const OnboardingWelcome = lazy(() => import('./pages/onboarding/OnboardingWelcome'));
const OnboardingPath = lazy(() => import('./pages/onboarding/OnboardingPath'));
const OnboardingViewMode = lazy(() => import('./pages/onboarding/OnboardingViewMode'));
const OnboardingFirstLecture = lazy(() => import('./pages/onboarding/OnboardingFirstLecture'));
const OnboardingReady = lazy(() => import('./pages/onboarding/OnboardingReady'));

// Subtle full-viewport spinner used while a lazy route chunk is downloading.
// Matches the dark IDE theme so there's no white flash when navigating from
// e.g. Login into the Dashboard.
const RouteFallback: React.FC = () => (
  <div
    role="status"
    aria-live="polite"
    aria-label="Loading page"
    style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      background: '#1e1e1e',
      color: '#e0e0e0',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: 14,
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        border: '2px solid #3a3a3a',
        borderTopColor: '#888',
        borderRadius: '50%',
        animation: 'route-fallback-spin 0.9s linear infinite',
      }}
    />
    <style>{`@keyframes route-fallback-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ZewstID OIDC config. Authority / client_id / scopes come from CRA env vars
// (REACT_APP_*); redirect URI is derived from window.location.origin so the
// same client config works for localhost dev (http://localhost:3000) and
// production (https://learn.hauraki.ai) without rebuilding.
const zewstidOidcConfig = {
  authority: process.env.REACT_APP_ZEWSTID_AUTHORITY || 'https://auth.zewstid.com/realms/zewstid',
  client_id: process.env.REACT_APP_ZEWSTID_CLIENT_ID || '',
  redirect_uri:
    typeof window !== 'undefined'
      ? `${window.location.origin}/auth/callback`
      : 'http://localhost:3000/auth/callback',
  post_logout_redirect_uri:
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000',
  scope: process.env.REACT_APP_ZEWSTID_SCOPE || 'openid profile email offline_access',
  response_type: 'code',
  // Keep tokens in sessionStorage so they survive page reloads but don't leak
  // across tabs / browser sessions. Avoids the long-lived footgun of
  // localStorage for access tokens. Refresh tokens stay protected by the
  // browser's storage isolation; for stricter posture, switch to memory-only
  // by removing the userStore line — at the cost of re-auth on every reload.
  userStore:
    typeof window !== 'undefined' ? new WebStorageStateStore({ store: window.sessionStorage }) : undefined,
  automaticSilentRenew: true,
  loadUserInfo: true,
  // Strip the `?code=&state=` from the URL after the SDK consumes them so
  // browser history doesn't carry the auth code around.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

// ── Legacy → Portal safety redirects ─────────────────────────────────────
// Students must never land on the old CoursePage / LearningItemPage / IDE
// course-detail surface (top-nav Dashboard/Catalog/My-Learning + "Ako"),
// even via a stale bookmark or a not-yet-repointed backend link. These
// wrappers carry the slug/labId + any query string through to the canonical
// /portal/* route with <Navigate replace> so the old URL leaves no history
// entry. The Dashboard / Catalog / My-Learning LIST routes are intentionally
// left alone — only the course/lab DETAIL routes redirect.
const LegacyCourseRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { search } = useLocation();
  return <Navigate to={`/portal/course/${slug}${search}`} replace />;
};

const LegacyCourseItemRedirect: React.FC = () => {
  const { slug, itemId } = useParams<{ slug: string; itemId: string }>();
  const { search } = useLocation();
  // The legacy `/course/:slug/:itemId` route carried the item id as a path
  // param; the portal course route reads the lesson from `?lesson=`. Best-
  // effort map itemId → lesson (correct when itemId is a lesson slug; for a
  // lab/assessment item id the shell still opens the course and resolves the
  // current lesson). Merge with any pre-existing query string.
  const params = new URLSearchParams(search);
  if (itemId) params.set('lesson', itemId);
  const qs = params.toString();
  return <Navigate to={`/portal/course/${slug}${qs ? `?${qs}` : ''}`} replace />;
};

const LegacyLabRedirect: React.FC = () => {
  const { labId } = useParams<{ labId: string }>();
  const { search } = useLocation();
  // Portal lab route also wants `?course=<slug>`; legacy `/lab/:labId` links
  // don't carry one, so a bare lab link lands on the in-portal "which course?"
  // notice — still inside the portal, never the old IDE. Any existing query
  // (e.g. ?env=, ?course=) is preserved.
  return <Navigate to={`/portal/lab/${labId}${search}`} replace />;
};

// Inner App: assumes AuthProvider is mounted above it. Gates protected routes
// on react-oidc-context's `isAuthenticated` — ZewstID is the sole identity
// provider post-cutover, so there's no legacy fallback to fall back to.
/**
 * Redirect an unauthenticated visitor to /login while remembering where they
 * were headed, so sign-in returns them to the deep link they wanted instead of
 * dumping them on a default page.
 */
function RequireAuthRedirect() {
  const location = useLocation();
  const fullPath = `${location.pathname}${location.search}${location.hash}`;
  return <Navigate to={loginHrefFor(fullPath)} replace />;
}

/**
 * An already-authenticated user hitting /login goes to their intended
 * destination (sanitized ?returnTo) or the portal home — never the legacy
 * IDE dashboard.
 */
function PostLoginRedirect() {
  const [search] = useSearchParams();
  return <Navigate to={resolvePostLogin(search.get('returnTo'))} replace />;
}

/**
 * First-run gate: an incomplete learner is routed into `/onboarding/welcome`
 * when they land on the portal home. Completion is account-scoped server
 * state, not browser storage. Deep links (e.g. a course URL from the checkout
 * handoff) bypass this because it wraps only the portal index.
 */
function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const { context, isLoading } = useUserContext();
  const access = useCourseTesterAccess(context?.user_id);
  if (isLoading || access.isLoading) return <RouteFallback />;
  if (access.error) return <CourseAccessCheckError onRetry={() => { void access.refresh(); }} />;
  return shouldRequireOnboarding(context, access.hasCourseTesterGrant)
    ? <Navigate to="/onboarding/welcome" replace />
    : <>{children}</>;
}

/**
 * Prevent a completed learner (or staff user) from re-entering onboarding
 * through a stale returnTo URL, bookmark, or legacy welcome-placeholder link.
 */
function RequireIncompleteOnboarding({ children }: { children: React.ReactNode }) {
  const { context, isLoading } = useUserContext();
  const access = useCourseTesterAccess(context?.user_id);
  if (isLoading || access.isLoading) return <RouteFallback />;
  if (access.error) return <CourseAccessCheckError onRetry={() => { void access.refresh(); }} />;
  if (context && !shouldRequireOnboarding(context, access.hasCourseTesterGrant)) {
    return <Navigate to="/portal" replace />;
  }
  return <>{children}</>;
}

function CourseAccessCheckError({ onRetry }: { onRetry: () => void }) {
  return (
    <section role="alert" className="mx-auto max-w-[640px] p-space-24 text-ink-primary">
      <h1 className="text-heading-28">Course access unavailable</h1>
      <p className="mt-space-12 text-body-16 text-ink-muted">We couldn’t check your approved course access. Try again; you do not need to repeat program onboarding.</p>
      <button type="button" onClick={onRetry} className="mt-space-16 rounded-card bg-accent-violet px-space-16 py-space-12 text-body-14 font-semibold text-white">Retry access check</button>
    </section>
  );
}

/**
 * SIGNUP-GATE (Cluster D) · Provisioning gate.
 *
 * Mounted ONLY once the ZewstID session is authenticated. Reads the same
 * `GET /users/me/context` the portal already reads and, when the session-manager
 * answered `403 { error:'signup_waitlisted' }` (a brand-new student whose region
 * is waitlist/closed), renders the Waitlisted screen instead of the app. The
 * person is signed in but has no learner record yet, so we must never drop them
 * into an empty portal — nor bounce them to /login, which they'd just bounce
 * back from.
 *
 * FAIL-OPEN by construction (cardinal safety — never lock anyone out): it blocks
 * ONLY on the explicit signup_waitlisted signal. While context is still loading,
 * when offline, on a plain auth error, or on any other failure, children render
 * exactly as before, so every non-waitlisted flow is unchanged. The context
 * fetch dedupes against the same window cache the portal pages already use.
 */
function ProvisionGate({ children }: { children: React.ReactNode }) {
  const { waitlist } = useUserContext();
  if (waitlist) {
    return <Waitlisted block={waitlist} />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  const oidc = useAuth();
  const location = useLocation();
  const isAuthenticated = oidc.isAuthenticated;

  // While react-oidc-context is still loading the user from sessionStorage
  // (typically <100ms after a hard reload), `isAuthenticated` is false and
  // `oidc.user` is undefined. If we let the route guards evaluate at this
  // point, every protected URL (e.g. /lab/:labId) immediately renders
  // <Navigate to="/login"/>; OIDC then resolves to "is authed", /login
  // sees that and re-Navigate's to /dashboard. Result: a hard reload of
  // any deep link bounces the user to /dashboard, losing query params
  // and the URL they actually wanted to load.
  //
  // Render the route-level spinner during the load window so the guards
  // can make their decision against a stable auth state.
  if (oidc.isLoading) {
    return <RouteFallback />;
  }

  const tree = (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/invitations/accept" element={<InvitationAcceptance />} />
        <Route path="/login" element={
          isAuthenticated ? <PostLoginRedirect /> : <Login />
        } />
        {/* MV5 Wave 1 · Portal Home v2 + Settings. Uses its own shell
            (PortalLayout) instead of the IDE-flavoured <Layout />. */}
        <Route
          path="/portal"
          element={
            isAuthenticated ? <PortalLayout /> : <RequireAuthRedirect />
          }
        >
          <Route index element={<RequireOnboarding><PortalHome /></RequireOnboarding>} />
          <Route path="settings" element={<PortalSettings />} />
          <Route path="help" element={<PortalHelp />} />
          {/* Portal · Cert Overview + Course Detail — designed on Figma
              page 04. The :id param is the cert or course slug; each child
              page fetches its own data from that slug — PortalCertOverview via
              portalApi.getCertOverview and PortalCourseDetail via the
              ShellRouter — so these routes are fully wired, not placeholders. */}
          <Route path="certs" element={<PortalProgram />} />
          <Route path="cert/:id" element={<PortalCertOverview />} />
          <Route path="course/:id" element={<PortalCourseDetail />} />
          {/* MV5 Phase 2c · Lab-entry variant of the portal shell. Takes
              `?course=<slug>` to know which course to load; the top-level
              /lab/:labId route (below) stays as-is for backward compat. */}
          <Route path="lab/:labId" element={<ShellLabWrapper />} />
          {/* Portal · Take Quiz — mirrors the /portal/lab/:labId pattern. The
              param is the quiz's course_item UUID (from tabs.quizzes[].id).
              Renders inside PortalLayout's chrome (it is NOT a full-viewport
              course/lab shell), so it reads as part of the portal. */}
          <Route path="quiz/:courseItemId" element={<PortalQuizPage />} />
          <Route path="tutor" element={<PortalTutor />} />
          <Route path="grades" element={<PortalGrades />} />
          <Route path="portfolio" element={<PortalPortfolio />} />
          {/* Sprint 1.C checkout success originally redirected here; keep the
              placeholder path as a fast redirect to /onboarding/welcome so
              any legacy URL that hasn't been repointed yet still lands
              correctly. */}
          <Route
            path="welcome-placeholder"
            element={<Navigate to="/onboarding/welcome" replace />}
          />
        </Route>

        {/* T1 buy→learn spine. `/checkout/:certSlug` is the enroll handoff the
            marketing site links to (auth-gated → sign-in returns here);
            `/checkout/success` is where Stripe returns, polling the webhook-
            written entitlement before landing the learner in /portal. */}
        <Route
          path="/checkout/success"
          element={
            isAuthenticated ? <PortalCheckoutReturn /> : <RequireAuthRedirect />
          }
        />
        {/* PK: JazzCash's return posts to the backend first (hash verified
            there), which then redirects the browser here. */}
        <Route
          path="/checkout/jazzcash/return"
          element={
            isAuthenticated ? <PortalJazzCashReturn /> : <RequireAuthRedirect />
          }
        />
        <Route
          path="/checkout/:certSlug"
          element={
            isAuthenticated ? <PortalCheckout /> : <RequireAuthRedirect />
          }
        />

        {/* MV5 Wave 1 · Onboarding v2 · Verticals. Own shell (no
            Companion / Course rails until the learner is inside a lecture). */}
        <Route
          path="/onboarding"
          element={
            isAuthenticated
              ? (
                <RequireIncompleteOnboarding>
                  <OnboardingLayout />
                </RequireIncompleteOnboarding>
              )
              : <RequireAuthRedirect />
          }
        >
          <Route index element={<Navigate to="welcome" replace />} />
          <Route path="welcome" element={<OnboardingWelcome />} />
          <Route path="path" element={<OnboardingPath />} />
          <Route path="view-mode" element={<OnboardingViewMode />} />
          <Route path="first-lecture" element={<OnboardingFirstLecture />} />
          <Route path="ready" element={<OnboardingReady />} />
        </Route>

        <Route path="/" element={
          isAuthenticated ? <Layout /> : <RequireAuthRedirect />
        }>
          {/* Phase 0 split-brain fix: the portal (/portal/*) is now the single
              student home. The legacy IDE-flavoured list pages
              (Dashboard/Catalog/MyLearning/Grades/Settings) are superseded and
              redirect into their /portal equivalents so there is one dashboard,
              one settings, one grades — reachable by direct URL too. The legacy
              components stay in the repo but are no longer mounted. */}
          <Route index element={<Navigate to="/portal" replace />} />
          <Route path="dashboard" element={<Navigate to="/portal" replace />} />
          <Route path="labs" element={<Navigate to="/portal/certs" replace />} />
          <Route path="my-labs" element={<Navigate to="/portal" replace />} />
          <Route path="grades" element={<Navigate to="/portal/grades" replace />} />
          <Route path="settings" element={<Navigate to="/portal/settings" replace />} />
          <Route path="progress" element={<Navigate to="/portal" replace />} />
          {/* Legacy course/lab DETAIL routes redirect into the portal shells. */}
          <Route path="course/:slug" element={<LegacyCourseRedirect />} />
          <Route path="course/:slug/:itemId" element={<LegacyCourseItemRedirect />} />
          <Route path="lab/:labId" element={<LegacyLabRedirect />} />
          {/* Kept: IDE environment host, profile, credentials (no portal twin yet). */}
          <Route path="environment/:environmentId" element={<IDE />} />
          <Route path="profile" element={<Profile />} />
          <Route path="credentials" element={<CredentialsPage />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
  );

  // Wrap the authenticated route tree in the provisioning gate so a signed-in
  // student blocked by the region signup-gate sees the Waitlisted screen instead
  // of the app. Unauthenticated visitors (Login / auth-callback) skip the gate
  // entirely — ProvisionGate reads /users/me/context, which is meaningless (and
  // would 401-toast) before sign-in.
  return (
    <Suspense fallback={<RouteFallback />}>
      {isAuthenticated && !['/invitations/accept', '/auth/callback'].includes(location.pathname) ? <ProvisionGate>{tree}</ProvisionGate> : tree}
    </Suspense>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider {...zewstidOidcConfig}>
        <ApiBridge />
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              style: { background: '#2d2d2d', color: '#e0e0e0', border: '1px solid #3a3a3a' },
              success: { iconTheme: { primary: '#22c55e', secondary: '#1e1e1e' } },
              error: { iconTheme: { primary: '#ef4444', secondary: '#1e1e1e' } },
            }}
          />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
